import axios from "axios";

const REACT_APP_ADMIN_API = process.env.REACT_APP_ADMIN_API;

export const getDevices = async (
  query,
  page = 1,
  cancelToken,
  filterBy = "",
  sortBy = "",
) => {
  const region = localStorage.getItem("region");
  let url = `${REACT_APP_ADMIN_API}/api/device?db=${region}&expand_status=true&q=${query}&results_per_page=100&page=${page}`;
  if (filterBy) {
    url = `${url}&${filterBy}`;
  }
  if (sortBy) {
    url = `${url}&sort=${sortBy}`;
  }
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "get",
      url,
      headers: { Authorization: `Bearer ${token}` },
      cancelToken,
    });
    return { data: response.data, hasNextPage: response.data.has_next_page };
  } catch (e) {
    return { detail: e.response?.data?.error };
  }
};

export const getDevicesByCustomer = async (
  customer,
  query,
  page = 1,
  filterBy = "",
) => {
  const region = localStorage.getItem("region");
  let url = `${REACT_APP_ADMIN_API}/api/customer/${customer}/device?db=${region}&expand_status=false&q=${query}&results_per_page=100&page=${page}`;
  if (filterBy) {
    url = `${REACT_APP_ADMIN_API}/api/customer/${customer}/device?db=${region}&expand_status=false&q=${query}&results_per_page=100&page=${page}&${filterBy}`;
  }
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "get",
      url,
      headers: { Authorization: `Bearer ${token}` },
    });
    return { data: response };
  } catch (e) {
    return { detail: e.response.data.error };
  }
};

export const getDevice = async (device) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  const region = localStorage.getItem("region");
  try {
    const response = await axios({
      method: "get",
      url: `${REACT_APP_ADMIN_API}/api/device/${device}?db=${region}`,
      headers: { Authorization: `Bearer ${token}` },
    });
    return { data: response.data };
  } catch (e) {
    return { detail: e.response.data.error };
  }
};

export const createDevice = async (
  customer,
  imei,
  template,
  hardware,
  sim_type,
  templateOverwrite,
  transformationOverwrite,
  variables,
) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  const region = localStorage.getItem("region");
  try {
    const response = await axios({
      method: "post",
      url: `${REACT_APP_ADMIN_API}/api/device?db=${region}`,
      headers: { Authorization: `Bearer ${token}` },
      data: {
        imei: imei,
        customer: customer,
        template: template,
        hardware_type: hardware,
        sim_type: sim_type?.toLowerCase(),
        template_overwrite: templateOverwrite,
        transformation_overwrite: transformationOverwrite,
        variables: variables,
      },
    });
    return { data: response };
  } catch (e) {
    if (e.response.data.code === 33) {
      return { errors: { imei: "Invalid IMEI" } };
    }
    return { detail: e.response.data.error };
  }
};

export const updateDevice = async (
  customer,
  imei,
  template,
  hardware,
  sim_type,
  templateOverwrite,
  transformationOverwrite,
  variables,
) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  const region = localStorage.getItem("region");
  try {
    const response = await axios({
      method: "put",
      url: `${REACT_APP_ADMIN_API}/api/device/${imei}?db=${region}`,
      headers: { Authorization: `Bearer ${token}` },
      data: {
        template: template,
        customer: customer,
        hardware_type: hardware,
        sim_type: sim_type?.toLowerCase(),
        template_overwrite: templateOverwrite,
        transformation_overwrite: transformationOverwrite,
        variables: variables,
      },
    });
    return {
      data: response.data,
    };
  } catch (e) {
    if (e.response.data.code === 33) {
      return { errors: { imei: "Invalid IMEI" } };
    }
    return { detail: e.response.data.error };
  }
};

export const deleteDevice = async (imei) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  const region = localStorage.getItem("region");
  try {
    const response = await axios({
      method: "delete",
      url: `${REACT_APP_ADMIN_API}/api/device/${imei}?db=${region}`,
      headers: { Authorization: `Bearer ${token}` },
    });
    return { data: response };
  } catch (e) {
    if (e.response.data.code === 120) {
      return {
        detail: "Cannot delete device before deleting connected vehicle",
      };
    }
    return { detail: e.response.data.error };
  }
};

export const bulkDeleteDevices = async (imeis: string[]) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "post",
      url: `${REACT_APP_ADMIN_API}/api/device/bulkdelete`,
      headers: { Authorization: `Bearer ${token}` },
      data: imeis.map((item) => { return { imei: item } })
    });
    return { data: response };
  } catch (e) {
    return { detail: e.response.data.error };
  }
};

export const getTelemetryByDevice = async (
  imei,
  page,
  fromDate,
  toDate,
  fromTime = "00:00",
  toTime = "00:00",
) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  const region = localStorage.getItem("region");
  try {
    const response = await axios({
      method: "get",
      url: `${REACT_APP_ADMIN_API}/api/device/${imei}/telemetry/${fromDate}T${fromTime}:00Z/${toDate}T${toTime}:00Z?page=${page}&db=${region}`,
      headers: { Authorization: `Bearer ${token}` },
    });
    return { data: response.data };
  } catch (e) {}
};

export const getFsmEventsByDevice = async (
  imei,
  fromDate,
  toDate,
  fromTime = "00:00",
  toTime = "00:00",
) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "get",
      url: `${REACT_APP_ADMIN_API}/api/device/${imei}/fsm_events/${fromDate}T${fromTime}:00Z/${toDate}T${toTime}:00Z`,
      headers: { Authorization: `Bearer ${token}` },
    });
    return { data: response.data };
  } catch (e) {}
};

export const getLogsByDevice = async (
  imei,
  fromDate,
  toDate,
  fromTime = "00:00",
  toTime = "00:00",
  query = "",
) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    let url = `${REACT_APP_ADMIN_API}/api/device/${imei}/logs/${fromDate}T${fromTime}:00Z/${toDate}T${toTime}:00Z`;
    if (query) {
      url = `${url}?q=${encodeURIComponent(query)}`;
    }
    const response = await axios({
      method: "get",
      url,
      headers: { Authorization: `Bearer ${token}` },
    });
    return { data: response.data };
  } catch (e) {
    return { error: e.response?.data?.error || "Failed to fetch logs" };
  }
};

export const createLogEntry = async (imei, content, created_by) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "post",
      url: `${REACT_APP_ADMIN_API}/api/device/${imei}/logs`,
      headers: { Authorization: `Bearer ${token}` },
      data: { content, created_by },
    });
    return { data: response.data };
  } catch (e) {
    return { error: e.response?.data?.error || "Failed to create log entry" };
  }
};

export const importDevices = async (devices, db) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "post",
      url: `${REACT_APP_ADMIN_API}/api/device/import?db=${db}`,
      headers: { Authorization: `Bearer ${token}` },
      data: devices,
    });
    return response;
  } catch (e) {
    return { e };
  }
};

export const sendSmsToDevice = async (imeis, command, ignition_on) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  try {
    const response = await axios({
      method: "post",
      url: `${REACT_APP_ADMIN_API}/api/device/smscommand`,
      headers: { Authorization: `Bearer ${token}` },
      data: { imeis, command, ignition_on },
    });
    return response;
  } catch (e) {
    return { e };
  }
};

export const downloadDevices = async (imeis, filterBy = "") => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  const imeisToSend = imeis === "all" ? "" : imeis;
  let url = `${REACT_APP_ADMIN_API}/api/device/export?imeis=${imeisToSend}`;
  if (filterBy) {
    url = `${url}&${filterBy}`;
  }
  try {
    const response = await axios({
      method: "get",
      url,
      headers: { Authorization: `Bearer ${token}` },
      responseType: "arraybuffer",
    });
    return response;
  } catch (e) {
    return { e };
  }
};

export const sendIotCommand = async (vehicle_uuids, command, no_sign = false, port_id = 0) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token"))
    ?.split("=")[1];
  let url = `${REACT_APP_ADMIN_API}/api/device/iotkey/send?port_id=${port_id}`;
  if (no_sign) {
    url = `${url}&no_sign=1`;
  }
  try {
    const response = await axios({
      method: "post",
      url,
      headers: { Authorization: `Bearer ${token}` },
      data: { vehicle_uuids, command },
    });
    return response;
  } catch (e) {
    return { e };
  }
};
